// Pantalla del asistente de correo (/assistant): tipos compartidos por la página y sus pestañas.
// Las filas son espejo de lo que devuelve la API (src/modules/assistant/web/repository.ts).
import { api } from '../../api/client';
import { useAuthStore } from '../../stores/authStore';

export type Tab =
  | 'chat'
  | 'emails'
  | 'mailboxes'
  | 'directives'
  | 'inbox'
  | 'cases'
  | 'actions'
  | 'capabilities'
  | 'decisions'
  | 'brain';

export interface LabeledKey {
  key: string;
  label: string;
}

/** Carpetas de sistema de Gmail en español; vacía o sin dato → "(sin carpeta)". */
const FOLDER_LABELS: Record<string, string> = {
  TRASH: 'Papelera',
  SPAM: 'Spam',
  INBOX: 'Recibidos',
};
export function folderLabel(folder: string | null | undefined): string {
  if (!folder) return '(sin carpeta)';
  return FOLDER_LABELS[folder] ?? folder;
}

/** GET /api/assistant/meta */
export interface AssistantMeta {
  types: LabeledKey[];
  statuses: LabeledKey[];
  folders: string[];
  /** Tramo 4 (opcionales: un servidor viejo no los manda; loadMeta los rellena vacíos). */
  case_kinds?: LabeledKey[];
  case_statuses?: LabeledKey[];
  action_modes?: LabeledKey[];
  /** Tramo 7 (opcionales: el servidor viejo no los manda). */
  capability_statuses?: LabeledKey[];
  capability_kinds?: LabeledKey[];
  /** Tipos de correo con acción real, por vendedor. */
  handler_types_by_seller?: Record<string, string[]>;
  learning_mode_by_seller?: Record<string, boolean>;
  /** Casos abiertos o esperando, en el alcance del usuario. */
  open_cases?: number;
}

export interface AttachmentMeta {
  filename: string;
  mime: string;
  size: number;
  path: string | null;
}

export interface EmailRow {
  id: number;
  seller_id: number;
  gmail_message_id: string;
  parent_email_id: number | null;
  folder: string | null;
  from_addr: string | null;
  subject: string | null;
  received_at: string;
  type: string | null;
  status: string;
  classified_by: string | null;
  confidence: string | number | null;
  case_id: number | null;
  attachments_count: number;
}

export type EmailDetail = EmailRow & {
  text_clean: string;
  payload: Record<string, unknown> | null;
  attachments: AttachmentMeta[];
  handler_summary: string | null;
  gmail_labels: string[];
  to_addr: string | null;
  gmail_thread_id: string | null;
};

export interface LastRun {
  status: string;
  started_at: string;
  duration_ms: number | null;
  stats: unknown;
}

export interface MailboxRow {
  id: number;
  seller_id: number;
  address: string;
  enabled: boolean;
  learning_mode: boolean;
  actions_enabled_since: string | null;
  last_sync_at: string | null;
  last_error: string | null;
  folders: Array<{ id: string; name: string }> | null;
}

export type MailboxWithStats = MailboxRow & { emails_total: number; last_run: LastRun | null };

export interface DirectiveRow {
  id: number;
  scope: string;
  seller_id: number | null;
  text: string;
  author: string;
  source: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface NotebookRow {
  id: number;
  category: string;
  seller_id: number | null;
  text: string;
  source: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface InboxRow {
  id: number;
  kind: string;
  seller_id: number | null;
  title: string;
  body: string;
  status: string;
  answer: string | null;
  answered_by: string | null;
  case_id: number | null;
  dedupe_key: string | null;
  created_at: string;
  answered_at: string | null;
}

/** Acción registrada del asistente (GET /api/assistant/actions, detalle de correo y de caso). */
export interface ActionRow {
  id: number;
  /** auto | chat | summary | manual */
  mode: string;
  seller_id: number | null;
  email_id: number | null;
  case_id: number | null;
  session_id: number | null;
  tool: string;
  target: string | null;
  input: unknown;
  ok: boolean | null;
  detail: string | null;
  duration_ms: number | null;
  actor: string | null;
  created_at: string;
  /** El servidor lo manda; si falta se calcula con isSimulated(). */
  simulated?: boolean;
}

/** true si la acción se simuló (modo aprendizaje): el detalle empieza con "SIMULADO: ". */
export function isSimulated(a: Pick<ActionRow, 'simulated' | 'detail'>): boolean {
  return a.simulated ?? !!a.detail?.startsWith('SIMULADO: ');
}

/** Detalle sin el prefijo "SIMULADO: " (la marca se muestra aparte). */
export function actionDetailText(a: Pick<ActionRow, 'detail'>): string {
  return (a.detail ?? '').replace(/^SIMULADO: /, '');
}

export type CaseKind =
  | 'support_case'
  | 'reimbursement_pending'
  | 'reimbursement_ambiguous'
  | 'category_doubt'
  | 'moderation_pending'
  | 'ip_unlinked'
  | 'claim_followup'
  | 'order_followup'
  | 'custom';
export type CaseStatus = 'open' | 'waiting' | 'resolved' | 'dismissed';

/** Caso que espera algo (GET /api/assistant/cases). */
export interface CaseRow {
  id: number;
  seller_id: number;
  kind: CaseKind | string;
  status: CaseStatus | string;
  title: string;
  data: Record<string, unknown> | null;
  next_check_at: string | null;
  attempts: number;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_note: string | null;
  created_at: string;
  updated_at: string;
  emails_count: number;
  last_email_at: string | null;
}

/** Correo enlazado a un caso (GET /api/assistant/cases/:id). */
export interface CaseEmailRow {
  id: number;
  seller_id: number;
  subject: string | null;
  from_addr: string | null;
  received_at: string;
  type: string | null;
  status: string;
  handler_summary: string | null;
}

/** Lista paginada { rows, total }; un servidor viejo puede mandar el arreglo plano. */
export function asPage<T>(data: unknown): { rows: T[]; total: number } {
  if (Array.isArray(data)) return { rows: data as T[], total: data.length };
  if (data && typeof data === 'object' && Array.isArray((data as { rows?: unknown }).rows)) {
    const d = data as { rows: T[]; total?: unknown };
    const total = Number(d.total);
    return { rows: d.rows, total: Number.isFinite(total) ? total : d.rows.length };
  }
  return { rows: [], total: 0 };
}

/** Etiquetas de tipo de caso (se completan con /meta; estos son los valores por defecto). */
export const CASE_KIND_LABEL: Record<string, string> = {
  support_case: 'Caso de soporte',
  reimbursement_pending: 'Reintegro pendiente',
  reimbursement_ambiguous: 'Reintegro ambiguo',
  category_doubt: 'Duda de categoría',
  moderation_pending: 'Moderación pendiente',
  ip_unlinked: 'Denuncia sin enlazar',
  claim_followup: 'Seguimiento de reclamo',
  order_followup: 'Seguimiento de venta',
  custom: 'Otro',
};
export const CASE_STATUS_LABEL: Record<string, string> = {
  open: 'Abierto',
  waiting: 'Esperando',
  resolved: 'Resuelto',
  dismissed: 'Descartado',
};
export const ACTION_MODE_LABEL: Record<string, string> = {
  auto: 'Automático',
  chat: 'Conversación',
  summary: 'Resumen',
  manual: 'Manual',
};
export const caseKindLabel = (k: string): string => CASE_KIND_LABEL[k] ?? k;
export const caseStatusLabel = (s: string): string => CASE_STATUS_LABEL[s] ?? s;
export const actionModeLabel = (m: string): string => ACTION_MODE_LABEL[m] ?? m;

/** Color del estado de un caso: abierto azul, esperando ámbar, resuelto verde, descartado gris. */
export function caseStatusTone(s: string): 'ok' | 'warning' | 'info' | 'unknown' {
  if (s === 'open') return 'info';
  if (s === 'waiting') return 'warning';
  if (s === 'resolved') return 'ok';
  return 'unknown';
}

/** Estado de un correo en palabras, para el bloque "Qué hizo el asistente". */
const EMAIL_STATE_WORDS: Record<string, string> = {
  new: 'Nuevo',
  classified: 'Clasificado',
  unclassified: 'Sin clasificar',
  done: 'Hecho',
  waiting: 'Esperando',
  failed: 'Falló',
  skipped: 'Omitido',
};
/** Una sola palabra por estado: la del servidor (/meta) si vino, si no la local. */
export const emailStateWords = (s: string): string => STATUS_LABEL[s] ?? EMAIL_STATE_WORDS[s] ?? s;

/** Qué consultó el asistente, en palabras ("Consultó: …"). */
export const TOOL_LABEL: Record<string, string> = {
  find_orders: 'ventas',
  get_order: 'una venta',
  get_order_live: 'una venta en Mercado Libre',
  find_purchases: 'compras',
  get_purchase: 'una compra',
  find_listings: 'publicaciones',
  get_listing_live: 'una publicación en Mercado Libre',
  find_claims: 'reclamos',
  find_ip_complaints: 'denuncias',
  search_emails: 'correos',
  read_email: 'un correo',
  list_mail_folders: 'carpetas de correo',
  get_health_overview: 'salud del sistema',
  list_process_runs: 'procesos',
  get_finance_summary: 'finanzas',
  sql_readonly: 'la base de datos',
  search_library: 'la biblioteca',
  read_library_doc: 'un documento',
  list_directives: 'las directivas',
};

/** Acciones del asistente: se muestran como frase sola, sin "Consultó:". */
export const ACTION_LABEL: Record<string, string> = {
  save_directive: 'Guardó una directiva',
  append_notebook: 'Anotó en el cuaderno',
  update_notebook: 'Actualizó una nota del cuaderno',
  post_to_inbox: 'Dejó un aviso en la bandeja',
  ask_owner: 'Te hizo una pregunta en la bandeja',
  set_case_status: 'Cambió el estado de un caso',
  // Acciones automáticas sobre correos (tramo 4)
  link_cashback_to_order: 'Vinculó un reintegro a una venta',
  enqueue_listing_fix: 'Mandó a corregir una publicación',
  sync_ip_complaints: 'Sincronizó las denuncias',
  // Acciones hechas desde la pantalla
  reclassify_email: 'Cambió el tipo de un correo',
  set_type: 'Cambió el tipo de un correo',
  set_mailbox: 'Cambió la configuración de una casilla',
  run_mailbox: 'Lanzó la lectura de una casilla',
  create_directive: 'Creó una directiva',
  edit_directive: 'Editó una directiva',
  create_note: 'Creó una nota del cuaderno',
  edit_note: 'Editó una nota del cuaderno',
  answer_inbox: 'Respondió un aviso de la bandeja',
  mark_inbox_read: 'Marcó un aviso como leído',
  link_order: 'Vinculó una venta',
  retry_handler: 'Reintentó la acción',
  handler: 'Manejador automático',
  decision: 'Decisión',
  link_cashback: 'Vinculó un reintegro',
};

const cleanToolName = (name: string): string => name.replace(/^mcp__cbt-assistant__/, '');

/** Línea de la conversación: acción como frase, consulta como "Consultó: …". */
export function toolLine(name: string): string {
  const clean = cleanToolName(name);
  return ACTION_LABEL[clean] ?? `Consultó: ${TOOL_LABEL[clean] ?? clean}`;
}

/** Nombre legible de una herramienta del asistente (registro de acciones). */
export function toolLabel(name: string): string {
  const clean = cleanToolName(name);
  if (ACTION_LABEL[clean]) return ACTION_LABEL[clean];
  if (TOOL_LABEL[clean]) return `Consultó: ${TOOL_LABEL[clean]}`;
  return humanizeKey(clean) || name;
}

/** Nombre interno sin etiqueta → guiones bajos por espacios y primera letra en mayúscula. */
export function humanizeKey(key: string): string {
  const words = key.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

/** Etiqueta de un dato extraído o de un caso. */
export const dataKeyLabel = (k: string): string => PAYLOAD_LABEL[k] ?? humanizeKey(k);

/** Conversación con el asistente (GET /api/assistant/sessions). */
export interface SessionRow {
  id: number;
  user_id: number | null;
  username: string;
  title: string | null;
  model: string;
  status: 'active' | 'closed';
  seller_ids: number[] | null;
  total_tokens_in: number;
  total_tokens_out: number;
  created_at: string;
  last_at: string;
  closed_at: string | null;
  /** Hay un turno corriendo en el servidor (ausente en el servidor viejo = no). */
  turn_active?: boolean;
  /** Avisos nuevos (sin ver) del usuario en esta conversación (ausente en el servidor viejo). */
  alerts_new?: number;
  /** Id del último mensaje guardado (para notar que llegó algo nuevo; ausente en el servidor viejo). */
  last_message_id?: number | string | null;
}

/**
 * Aviso que el asistente deja por su cuenta en la conversación ("el asistente habla primero").
 * Espejo de `AlertContent` en src/modules/assistant/alerts/types.ts.
 */
export interface AlertCard {
  id: number;
  kind: string;
  /** Tipo de aviso en español. */
  label: string;
  seller_id: number;
  seller_name: string;
  title: string;
  /** Texto simple (no Markdown). */
  body: string;
  entity_kind: string | null;
  entity_id: string | null;
  status: 'new' | 'seen' | 'followed' | 'dismissed' | string;
  /** Si el asistente lo revisa solo: none | pending | started | done | skipped. */
  auto_turn: 'none' | 'pending' | 'started' | 'done' | 'skipped' | string;
  auto_turn_note: string | null;
  /** Quién lo cerró: owner (botón) | assistant (concluyó que no hay nada que hacer) | system (el reclamo ya figura cerrado). */
  resolved_by?: 'owner' | 'assistant' | 'system' | string | null;
  resolved_note?: string | null;
  created_at: string;
}

/**
 * Aviso pendiente visto desde la vista única de todas las conversaciones
 * (GET /api/assistant/sessions/alerts/pending, 2026-10-21): la tarjeta más su conversación.
 */
export interface PendingAlert extends AlertCard {
  session_id: number | null;
  session_title: string | null;
}

export type MessageBlock =
  | { type: 'text'; text: string }
  | { type: 'tool_use'; name: string; input_summary: string };

/**
 * Acción que el asistente propuso en la conversación (tramo 6) y espera confirmación del dueño.
 * Espejo de `PendingCard` en src/modules/assistant/agent/routes.ts.
 */
export interface PendingCard {
  id: number;
  kind: string;
  /** Tipo de acción en español. */
  label: string;
  /** Vista previa exacta de lo que se va a hacer (Markdown). */
  preview: string;
  status: 'pending' | 'confirmed' | 'executed' | 'failed' | 'cancelled' | 'expired' | string;
  /** No se puede deshacer (mensajes, respuestas): el botón pide dos clics. */
  irreversible: boolean;
  expires_at: string;
  created_at: string;
  executed_at: string | null;
  /** Resultado (ejecutada) o motivo del fallo. */
  detail: string | null;
  /** El usuario tiene el permiso de ese tipo de acción. */
  can_confirm: boolean;
  /** Qué verificó el asistente antes de proponer (ausente en el servidor viejo). */
  verificado?: string | null;
  /** Solo en tarjetas de reclamo: el reclamo asociado (ausente en el servidor viejo). */
  params?: { claim_id?: number } | null;
}

/**
 * Lo que permite Mercado Libre ahora para el reclamo de una tarjeta
 * (GET /api/assistant/sessions/:id/actions/:pid/claim-options).
 */
export interface ClaimOptions {
  claim_id: number;
  status?: string | null;
  stage?: string | null;
  /** Tipos de resolución permitidos (allow_return, claim_refund, claim_partial_refund, open_dispute). */
  allowed: string[];
  offers: Array<{ percentage: number; amount: number | null; currency: string | null }>;
  delivered: boolean;
  /** Aviso para el dueño (entregado, o Mercado Libre no respondió); null = sin aviso. */
  warning: string | null;
  /** El usuario no tiene el permiso de resolver reclamos: no se muestra la fila. */
  no_permission?: true;
  /** Costo de la devolución (solo si se puede aceptar); null = no se pudo conocer. */
  return_cost?: { amount: number; currency: string; amount_usd: number | null } | null;
  /** Frase del costo para la confirmación de "Aceptar devolución"; null = no aplica. */
  return_cost_text?: string | null;
  /** Casos de seguimiento con notas sobre este reclamo: se muestra ANTES de los botones; null = sin aviso. */
  cases_warning?: string | null;
}

/** Mensaje guardado de una conversación (GET /api/assistant/sessions/:id). */
export interface MessageRow {
  /** bigserial: llega como texto desde la base. */
  id: number | string;
  session_id: number;
  role: 'user' | 'assistant' | 'system';
  content:
    | { text: string }
    | { blocks: MessageBlock[] }
    | { error: string }
    /** Aviso del asistente (mensaje `system`). */
    | { alert: AlertCard };
  created_at: string;
}

/** Etiquetas de tipo de correo (clave → texto). Se llena con loadMeta() desde /api/assistant/meta. */
export const TYPE_LABEL: Record<string, string> = {};
/** Etiquetas de estado de correo (clave → texto). Se llena con loadMeta(). */
export const STATUS_LABEL: Record<string, string> = {};

/**
 * Etiquetas de los pedidos de capacidad (tramo 7). La fuente es el servidor (/meta
 * `capability_statuses` / `capability_kinds`, que loadMeta() copia acá); estos valores son solo el
 * respaldo mientras carga o si el servidor es viejo.
 */
export const CAPABILITY_STATUS_LABEL: Record<string, string> = {
  requested: 'Pedido',
  building: 'Construyendo',
  gate: 'Pasando pruebas',
  review: 'En revisión',
  fixing: 'Corrigiendo',
  deploying: 'Desplegando',
  done: 'Lista',
  failed: 'Falló',
  needs_owner: 'Necesita al dueño',
  rejected: 'Rechazado / revertido',
  cancelled: 'Cancelado',
};
export const CAPABILITY_KIND_LABEL: Record<string, string> = {
  tool: 'Herramienta de consulta',
  action: 'Acción con confirmación',
  integration: 'Conexión nueva',
  screen: 'Pantalla',
  revert: 'Deshacer una capacidad',
  other: 'Otro',
};

let metaPromise: Promise<AssistantMeta | null> | null = null;

/** Trae /api/assistant/meta una sola vez y llena TYPE_LABEL / STATUS_LABEL. */
export function loadMeta(): Promise<AssistantMeta | null> {
  if (!metaPromise) {
    metaPromise = api
      .get<AssistantMeta>('/assistant/meta')
      .then((res) => {
        if (!res.success || !res.data) {
          metaPromise = null;
          return null;
        }
        const m: AssistantMeta = {
          ...res.data,
          case_kinds: res.data.case_kinds ?? [],
          case_statuses: res.data.case_statuses ?? [],
          action_modes: res.data.action_modes ?? [],
          handler_types_by_seller: res.data.handler_types_by_seller ?? {},
          learning_mode_by_seller: res.data.learning_mode_by_seller ?? {},
          open_cases: Number(res.data.open_cases) || 0,
        };
        for (const t of m.types) TYPE_LABEL[t.key] = t.label;
        for (const s of m.statuses) STATUS_LABEL[s.key] = s.label;
        for (const k of m.case_kinds ?? []) CASE_KIND_LABEL[k.key] = k.label;
        for (const s of m.case_statuses ?? []) CASE_STATUS_LABEL[s.key] = s.label;
        for (const a of m.action_modes ?? []) ACTION_MODE_LABEL[a.key] = a.label;
        for (const c of m.capability_statuses ?? []) CAPABILITY_STATUS_LABEL[c.key] = c.label;
        for (const c of m.capability_kinds ?? []) CAPABILITY_KIND_LABEL[c.key] = c.label;
        return m;
      })
      .catch(() => {
        metaPromise = null;
        return null;
      });
  }
  return metaPromise;
}

/** Vuelve a pedir /meta (p. ej. para refrescar el contador de casos abiertos tras resolver uno). */
export function refreshMeta(): Promise<AssistantMeta | null> {
  metaPromise = null;
  return loadMeta();
}

/** Opciones de una lista de /meta, o las etiquetas por defecto si el servidor no las manda. */
export function optionsOf(
  list: LabeledKey[] | undefined,
  fallback: Record<string, string>
): LabeledKey[] {
  if (list && list.length > 0) return list;
  return Object.entries(fallback).map(([key, label]) => ({ key, label }));
}

export const typeLabel = (key: string | null): string =>
  key ? (TYPE_LABEL[key] ?? key) : 'Sin clasificar';
export const statusLabel = (key: string): string => STATUS_LABEL[key] ?? key;

/** Color del estado en la tabla y el detalle. */
export function statusTone(status: string): 'ok' | 'warning' | 'error' | 'info' {
  // "Resuelto" (done) cuenta como procesado, igual que en el resumen de la pestaña Correos.
  if (status === 'classified' || status === 'done') return 'ok';
  if (status === 'unclassified') return 'warning';
  if (status === 'failed') return 'error';
  return 'info';
}

/** Quién escribió una directiva / nota / aviso, en palabras. */
export const SOURCE_LABEL: Record<string, string> = {
  claims: 'Bot de reclamos',
  owner: 'Dueño',
  agent: 'Asistente',
  claude: 'Claude',
};
export const sourceLabel = (s: string | null | undefined): string =>
  s ? (SOURCE_LABEL[s] ?? s) : '—';

/** Tipo de aviso de la bandeja, en palabras. */
export const INBOX_KIND_LABEL: Record<string, string> = {
  question: 'Pregunta',
  notice: 'Aviso',
  summary: 'Resumen',
  order_followup: 'Respuesta del comprador',
};

/**
 * true si el usuario ve todas las empresas: solo él puede escribir entradas globales
 * (el servidor lo vuelve a controlar y responde con su propio mensaje si no).
 */
export function useSeesAllSellers(): boolean {
  return useAuthStore((s) => {
    const u = s.user;
    if (!u) return false;
    // Mismo criterio que el servidor (sellerScopeOf): admin o permiso "ver todos los vendedores".
    return u.is_admin || (u.capabilities ?? []).includes('global.all_sellers');
  });
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Nombres en español de los datos extraídos más comunes. Los demás se muestran con su clave. */
export const PAYLOAD_LABEL: Record<string, string> = {
  product_title: 'Producto',
  old_category_path: 'Categoría anterior',
  suggested_category_path: 'Categoría sugerida',
  case_id: 'Nº de caso',
  order_ids: 'Ventas',
  operation_ids: 'Operaciones',
  pairs: 'Ventas ↔ operaciones',
  ambiguous_operations: 'Operaciones ambiguas',
  claim_id: 'Nº de reclamo',
  reason: 'Motivo',
  reason_text: 'Motivo',
  listing_id: 'Publicación',
  listing_url: 'Enlace de la publicación',
  site_id: 'País',
  source: 'Origen',
  // Datos de un caso
  ml_case_id: 'Nº de caso de Mercado Libre',
  ambiguous: 'Operaciones ambiguas',
  orders_without_operation: 'Ventas sin operación',
  linked: 'Ya vinculadas',
  pending: 'Pendientes',
  expires_at: 'Se deja de esperar el',
  unknown_orders: 'Ventas que no existen en el sistema',
  conflicts: 'Conflictos',
  candidates: 'Publicaciones candidatas',
  synced: 'Denuncias ya sincronizadas',
  site_item_id: 'Publicación en el país',
  cbt_item_id: 'Publicación global',
  asin: 'Producto de Amazon',
  brand: 'Marca',
  title: 'Título',
  subgroup: 'Motivo de moderación',
};

/** Nombres en español de los datos de un caso (pestaña Casos). Lo que no figura cae en `dataKeyLabel`. */
export const CASE_DATA_LABEL: Record<string, string> = {
  ml_case_id: 'Caso de soporte ML',
  pairs: 'Pares venta↔operación',
  linked: 'Vinculados',
  pending: 'Pendientes',
  conflicts: 'Conflictos',
  ambiguous: 'Ambiguas',
  ambiguous_only: 'Solo ambiguas',
  unknown_orders: 'Ventas desconocidas',
  orders_without_operation: 'Ventas sin operación',
  expires_at: 'Vence',
  candidates: 'Candidatas',
  product_title: 'Título',
  listing_id: 'Publicación',
  old_category_path: 'Categoría anterior',
  suggested_category_path: 'Categoría sugerida',
  live_status: 'Estado en vivo',
  reason_text: 'Motivo',
  listing: 'Publicación',
  synced: 'Sincronizado',
  listing_key: 'Clave',
  // Seguimiento de reclamo (claim_followup)
  notes: 'Notas',
  claim_id: 'Reclamo',
  ml_order_id: 'Venta',
  next_step: 'Próximo paso',
  deadline: 'Plazo',
  decision: 'Decisión del dueño',
  summary: 'Resumen',
  stage: 'Etapa',
  links: 'Enlaces',
};

/** Fecha corta dd/mm hh:mm (notas de un caso). */
export function fmtShortDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export const caseDataKeyLabel = (k: string): string => CASE_DATA_LABEL[k] ?? dataKeyLabel(k);

/** Valor de un dato extraído, legible (listas separadas por coma, pares como "a ↔ b"). */
export function fmtValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) {
    if (v.length === 0) return '—';
    return v
      .map((x) => {
        if (x && typeof x === 'object' && !Array.isArray(x)) {
          const vals = Object.values(x as Record<string, unknown>).map((y) => fmtValue(y));
          return vals.join(' ↔ ');
        }
        return fmtValue(x);
      })
      .join(', ');
  }
  if (typeof v === 'object') {
    return Object.entries(v as Record<string, unknown>)
      .map(([k, val]) => `${dataKeyLabel(k)}: ${fmtValue(val)}`)
      .join(' · ');
  }
  if (typeof v === 'boolean') return v ? 'Sí' : 'No';
  // Fechas guardadas como texto ISO → fecha legible.
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) return fmtDateTime(v);
  return String(v);
}

/** Secciones de la ficha del análisis de un reclamo (todas texto, pueden faltar). */
export interface ClaimAnalysisSections {
  plazo?: string;
  hechos?: string;
  dice_el_comprador?: string;
  premisa?: string;
  hipotesis?: string;
  plata?: string;
  precedentes?: string;
  opciones?: string;
  recomendacion?: string;
  respuesta_propuesta?: string;
  que_sigue?: string;
}

/** Ficha del análisis de un reclamo. Espejo de `AnalysisRow` en src/modules/assistant/alerts/claim-analyses.ts. */
export interface AnalysisCard {
  id: number;
  seller_id: number;
  ml_claim_id: string;
  session_id: number | null;
  version: number;
  data_up_to: Record<string, unknown> | null;
  alert_ids: number[] | null;
  summary_line: string;
  sections: ClaimAnalysisSections;
  proposal_id: number | null;
  status: string;
  created_at: string;
}

/** Voto de convivencia asistente nuevo vs agente viejo (POST /api/assistant/claims/:s/:c/vote). */
export interface VoteRow {
  id: number;
  vote: 'nuevo' | 'igual' | 'viejo' | 'ambos_mal';
  comment: string | null;
  analysis_id: number | null;
  created_at: string;
}

export type ClaimRefreshState = 'queued' | 'running' | 'none' | 'failed';

/** Respuesta de GET /api/assistant/claims/:sellerId/:claimId/thread. */
export interface ClaimThread {
  session: { id: number; title: string | null } | null;
  analysis: AnalysisCard | null;
  /** Motivo por el que se oculta la ficha (sin permiso para ver importes); null = se ve. */
  hidden: string | null;
  fresh: {
    stale: boolean;
    reason: string | null;
    claim_status: string | null;
    /** Reanálisis de la ficha vieja: en cola, corriendo, nada en camino o terminó sin ficha nueva. */
    refresh: ClaimRefreshState;
  };
  pending: PendingCard[];
  my_vote: VoteRow | null;
}
